import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import apiClient from "../../../config/apiClient";
import FaviconImg from "../../../assets/new-images/Logo.ico";
import "./ReDesign.css";
import { useLocation } from "react-router-dom";

import {
  ChevronRight,
  Clock,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  Search,
  Bookmark,
  Share2,
  Printer,
  Home,
  BookOpen,
  FileText,
  ExternalLink,
  Eye,
  HelpCircle,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Code,
  List,
  Zap,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Maximize2,
  Calendar,
  User,
  Tag,
  Filter,
  Layout,
  ArrowUp,
  Link as LinkIcon,
} from "lucide-react";
import DocsWithTOC from "./DocsWithTOC";
import { Helmet } from "react-helmet";

const Skel = ({ className = "" }) => (
  <div className={`animate-pulse rounded bg-gray-200 ${className}`} />
);

const DocsPageSkeleton = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="flex">
        {/* MAIN CONTENT SKELETON */}
        <main className="flex-1 min-w-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
            <Skel className="h-10 w-3/4 mb-4" />
            <Skel className="h-6 w-2/3 mb-8" />

            <div className="space-y-6">
              <Skel className="h-5 w-full" />
              <Skel className="h-5 w-11/12" />
              <Skel className="h-5 w-10/12" />

              <Skel className="h-8 w-1/2 mt-10" />
              <Skel className="h-5 w-full" />
              <Skel className="h-5 w-11/12" />

              <Skel className="h-56 w-full rounded-xl mt-8" />

              <Skel className="h-8 w-2/5 mt-10" />
              <Skel className="h-5 w-full" />
              <Skel className="h-5 w-11/12" />
              <Skel className="h-5 w-9/12" />
            </div>
          </div>

          <div className="btn_side px-4 sm:px-6 pb-10">
            <div className="navCard is-disabled">
              <Skel className="h-4 w-24 mb-3" />
              <Skel className="h-4 w-52" />
            </div>
            <div className="navCard is-disabled">
              <Skel className="h-4 w-20 mb-3" />
              <Skel className="h-4 w-56" />
            </div>
          </div>
        </main>

        {/* RIGHT TOC SKELETON */}
        <aside className="hidden xl:block w-80 pl-8 pr-4 py-8">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <Skel className="h-5 w-28" />
              <Skel className="h-6 w-20 rounded-full" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skel key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const iconFor = (key) => {
  const ICONS = {
    document: "📘",
    check: "✔️",
    users: "👥",
    plug: "🔌",
    card: "💳",
    lock: "🔒",
    rocket: "🚀",
    bulb: "💡",
    settings: "⚙️",
    chart: "📊",
    target: "🎯",
    star: "🌟",
  };
  return ICONS[key] || "📄";
};

const getScrollParent = (el) => {
  let parent = el?.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return window;
};

const HEADER_OFFSET = 90;

const scrollToHeading = (id) => {
  const el = document.getElementById(id);
  if (!el) return;

  const scroller = getScrollParent(el);

  // ✅ Update hash without browser jump
  window.history.replaceState(null, "", `#${id}`);

  if (scroller === window) {
    const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
    return;
  }

  const containerRect = scroller.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const top =
    elRect.top - containerRect.top + scroller.scrollTop - HEADER_OFFSET;

  scroller.scrollTo({ top, behavior: "smooth" });
};

const renderBlockContent = (content, block = null) => {
  if (!content) return null;

  if (Array.isArray(content)) {
    return (
      <>
        {content.map((inline, i) => {
          if (typeof inline === "string") return inline;

          if (inline.type === "text") {
            let textElement = inline.text;

            if (inline.styles) {
              if (inline.styles.bold) {
                textElement = (
                  <strong key={i} className="font-bold text-gray-900">
                    {textElement}
                  </strong>
                );
              }
              if (inline.styles.italic) {
                textElement = (
                  <em key={i} className="italic text-gray-800">
                    {textElement}
                  </em>
                );
              }
              if (inline.styles.underline) {
                textElement = (
                  <u key={i} className="underline text-gray-800">
                    {textElement}
                  </u>
                );
              }
              if (inline.styles.strikethrough) {
                textElement = (
                  <s key={i} className="line-through text-gray-500">
                    {textElement}
                  </s>
                );
              }
              if (inline.styles.code) {
                textElement = (
                  <code
                    key={i}
                    className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded font-mono text-sm"
                  >
                    {textElement}
                  </code>
                );
              }
            }

            return textElement;
          }

          if (inline.type === "link") {
            return (
              <a
                key={i}
                href={inline.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors inline-flex items-center gap-1"
              >
                {inline.content?.[0]?.text || inline.href}
                <ExternalLink size={14} />
              </a>
            );
          }

          return null;
        })}
      </>
    );
  }

  if (block?.children?.[0]?.text) return block.children[0].text;

  return null;
};

const renderBlock = (block, index) => {
  if (!block || typeof block !== "object") return null;

  // BlockNote paragraph
  if (
    block.type === "paragraph" &&
    block.content &&
    Array.isArray(block.content)
  ) {
    return (
      // mb-6 text-gray-700 leading-relaxed text-base md:text-lg
      <p className="Docs_Para_Note_Class">
        {block.content.map((inline, i) => {
          if (inline.type === "text") {
            let textElement = inline.text;
            if (inline.styles) {
              if (inline.styles.bold) {
                textElement = (
                  <strong key={i} className="font-bold text-gray-900">
                    {textElement}
                  </strong>
                );
              }
              if (inline.styles.italic) {
                textElement = (
                  <em key={i} className="italic text-gray-800">
                    {textElement}
                  </em>
                );
              }
              if (inline.styles.underline) {
                textElement = (
                  <u key={i} className="underline text-gray-800">
                    {textElement}
                  </u>
                );
              }
              if (inline.styles.strikethrough) {
                textElement = (
                  <s key={i} className="line-through text-gray-500">
                    {textElement}
                  </s>
                );
              }
              if (inline.styles.code) {
                textElement = (
                  <code
                    key={i}
                    className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded font-mono text-sm"
                  >
                    {textElement}
                  </code>
                );
              }
            }
            return textElement;
          }

          if (inline.type === "link") {
            return (
              <a
                key={i}
                href={inline.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors inline-flex items-center gap-1"
              >
                {inline.content?.[0]?.text || inline.href}
                <ExternalLink size={14} />
              </a>
            );
          }

          return null;
        })}
      </p>
    );
  }

  // headings
  if (block.type === "heading") {
    const level = block.props?.level || 2;
    const Tag = `h${level}`;
    let headingText = "";

    if (Array.isArray(block.content)) {
      headingText = block.content.map((item) => item.text || "").join("");
    }

    const headingClasses = {
      1: "text-[28px] font-semibold text-gray-900 mb-4 mt-8",
      2: "text-[22px] font-semibold text-gray-900 mb-4 mt-8",
      3: "text-[18px] font-semibold text-gray-800 mb-4 mt-4",
      4: "text-xl md:text-2xl font-semibold text-gray-800 mb-3 mt-10", // keep as is
      5: "text-lg md:text-xl font-semibold text-gray-700 mb-2 mt-10",
      6: "text-base md:text-lg font-semibold text-gray-700 mb-2 mt-10",
    };

    const hid = block.id || `h${level}-${index}`;

    return (
      <Tag
        id={hid}
        className={`${headingClasses[level] || headingClasses[2]} scroll-mt-24 group relative`}
      >
        {headingText}
        <a
          href={`#${hid}`}
          onClick={(e) => {
            e.preventDefault();
            scrollToHeading(hid);
          }}
          className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-600"
          aria-label="Direct link to this section"
        >
          <LinkIcon size={16} />
        </a>
      </Tag>
    );
  }

  switch (block.type) {
    case "bulletListItem":
    case "numberedListItem":
      return (
        <li className="mb-2 ml-6 text-gray-700 leading-relaxed">
          {renderBlockContent(block.content)}
        </li>
      );

    case "bulletList":
      return (
        <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
          {block.children?.map((child, i) => renderBlock(child, i))}
        </ul>
      );

    case "numberedList":
      return (
        <ol className="list-decimal bg-red-900 pl-6 mb-8 space-y-2 text-gray-700">
          {block.children?.map((child, i) => renderBlock(child, i))}
        </ol>
      );

    case "image": {
      const imageUrl = block.props?.url || block.src;
      const caption = block.props?.caption || "";
      return (
        <div className="my-10">
          <div className="relative group">
            <img
              src={imageUrl}
              alt={caption}
              className="max-w-full rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-200"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
            <button
              onClick={() => window.open(imageUrl, "_blank")}
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white border border-gray-200"
              aria-label="Open image in fullscreen"
              type="button"
            >
              <Maximize2 size={18} className="text-gray-700" />
            </button>
          </div>
          {caption && (
            <p className="text-center text-sm text-gray-500 mt-3 italic">
              {caption}
            </p>
          )}
        </div>
      );
    }

    case "codeBlock": {
      const code = block.props?.code || block.content?.[0]?.text || "";
      const language = block.props?.language || "javascript";
      return (
        <div className="my-8 relative group">
          <div className="flex items-center justify-between bg-gray-800 text-gray-200 px-4 py-2 rounded-t-lg border-b border-gray-700">
            <div className="flex items-center gap-2">
              <Code size={16} />
              <span className="font-mono text-sm">{language}</span>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(code);
                alert("Code copied to clipboard!");
              }}
              className="text-gray-400 hover:text-white transition-colors p-1"
              aria-label="Copy code"
              type="button"
            >
              <Copy size={16} />
            </button>
          </div>
          <pre className="bg-gray-800 text-gray-200 p-4 rounded-b-lg overflow-x-auto text-sm md:text-base">
            <code>{code}</code>
          </pre>
        </div>
      );
    }

    case "blockquote":
      return (
        <blockquote className="border-l-4 border-blue-600 pl-6 py-2 my-8 italic text-gray-700 bg-blue-50 rounded-r-lg">
          {renderBlockContent(block.content, block)}
        </blockquote>
      );
    // Add this case for audio blocks - FULLY RESPONSIVE
    case "audio": {
      const audioUrl = block.props?.url;
      const audioName = block.props?.name || "Audio file";
      const showPreview = block.props?.showPreview || false;

      if (!audioUrl) return null;

      return (
        <div className="my-4 sm:my-6 md:my-8 p-4 sm:p-5 md:p-6 bg-gray-50 rounded-xl border border-gray-200 overflow-x-hidden">
          {/* Header section - stacks on mobile, flex on larger screens */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4">
            {/* Icon - centered on mobile, left on larger screens */}
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mx-auto sm:mx-0">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                />
              </svg>
            </div>

            {/* Text content - centered on mobile, left on larger screens */}
            <div className="text-center sm:text-left flex-1">
              <h4 className="font-medium text-gray-900 text-base sm:text-lg">
                Audio
              </h4>
              <p className="text-xs sm:text-sm text-gray-600 break-words">
                {audioName}
              </p>
            </div>
          </div>

          {/* Audio player - FULLY RESPONSIVE */}
          <div className="w-full">
            <audio
              controls
              className="w-full h-10 sm:h-12 md:h-14" // Responsive height
              preload={showPreview ? "metadata" : "none"}
            >
              <source src={audioUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>

          {/* Caption - responsive text */}
          {block.props?.caption && (
            <p className="text-xs sm:text-sm text-gray-500 mt-3 italic text-center sm:text-left">
              {block.props.caption}
            </p>
          )}
        </div>
      );
    }

    case "video": {
      const videoUrl = block.props?.url;
      const videoName = block.props?.name || "Video file";

      if (!videoUrl) return null;

      // Check if it's a YouTube URL
      const isYouTube =
        videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be");

      if (isYouTube) {
        // Extract YouTube ID
        const getYouTubeId = (url) => {
          const regExp =
            /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
          const match = url.match(regExp);
          return match && match[2].length === 11 ? match[2] : null;
        };

        const videoId = getYouTubeId(videoUrl);

        if (!videoId) {
          return <div className="text-red-500">Invalid YouTube URL</div>;
        }

        return (
          <div className="my-4 sm:my-6 md:my-8">
            <div className="relative" style={{ paddingBottom: "56.25%" }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-xl"
                src={`https://www.youtube.com/embed/${videoId}`}
                allowFullScreen
              />
            </div>
          </div>
        );
      }

      // Regular video file (MP4 etc)
      return (
        <div className="my-4 sm:my-6 md:my-8">
          <div className="relative" style={{ paddingBottom: "56.25%" }}>
            <video
              controls
              className="absolute top-0 left-0 w-full h-full rounded-xl"
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          </div>
        </div>
      );
    }

    default: {
      const content = renderBlockContent(block.content, block);
      return content ? (
        <div className="my-4 text-gray-700">{content}</div>
      ) : null;
    }
  }
};

const InfoBox = ({ type, children }) => {
  const config = {
    info: {
      icon: AlertCircle,
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-800",
      iconColor: "text-blue-600",
    },
    warning: {
      icon: AlertCircle,
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      text: "text-yellow-800",
      iconColor: "text-yellow-600",
    },
    success: {
      icon: CheckCircle,
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-800",
      iconColor: "text-green-600",
    },
    tip: {
      icon: Zap,
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-800",
      iconColor: "text-purple-600",
    },
    note: {
      icon: FileText,
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-800",
      iconColor: "text-gray-600",
    },
  };

  const {
    icon: Icon,
    bg,
    border,
    text,
    iconColor,
  } = config[type] || config.info;

  return (
    <div className={`my-6 p-5 rounded-xl border-l-4 ${border} ${bg} ${text}`}>
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 flex-shrink-0 ${iconColor}`} size={20} />
        <div className="flex-1">
          <p className="font-medium capitalize mb-1">{type}</p>
          <div className="text-sm text-gray-700">{children}</div>
        </div>
      </div>
    </div>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-4 my-8">
    <div className="flex-shrink-0">
      <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
        {number}
      </div>
    </div>
    <div className="flex-1">
      <h4 className="text-xl font-semibold text-gray-900 mb-2">{title}</h4>
      <div className="text-gray-700">{children}</div>
    </div>
  </div>
);

/* -------------------------------------------
   ✅ MAIN COMPONENT
-------------------------------------------- */
export default function DocsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [openCategories, setOpenCategories] = useState({});
  const [contentBlocks, setContentBlocks] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bookmarked, setBookmarked] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [viewMode, setViewMode] = useState("default");

  const contentRef = useRef(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  // const location = useLocation();
  // const doc_id = location.state?.id;
  const tocItemRefs = useRef({}); // { [id]: HTMLAnchorElement }
  const [tocIndicator, setTocIndicator] = useState({ top: 8, height: 20 });
  // Fetch single page
  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        // const res = await apiClient.get(`/pages/${slug}?status=published`);
        const res = await apiClient.get(`/pages/${slug}?status=published`);
        // const res = await apiClient.get(`categories/${id}`);
        const data = res.data.data ?? res.data;

        // console.log("DOCS PAGE AAA", data);

        // if (!data || !data.pages || data.pages.length === 0) {
        //   navigate("/docs", { replace: true });
        //   return;
        // }

        const pageData = data;

        if (!pageData) {
          // navigate("/docs", { replace: true });
          return;
        }

        setPage(pageData);

        let blocks = [];
        if (data.description) {
          try {
            const parsed =
              typeof data.description === "string"
                ? JSON.parse(data.description)
                : data.description;

            blocks = Array.isArray(parsed) ? parsed : [parsed];
          } catch (e) {
            console.error("Failed to parse description:", e);
          }
        }

        setContentBlocks(blocks);
      } catch (err) {
        // if (err.response?.status === 404) {
        //     setError("Document not found. It may have been deleted or unpublished.");
        // } else {
        //     setError("Failed to load page: " + (err.message || "Unknown error"));
        // }
        // navigate("/docs", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);
  useEffect(() => {
    const FetchData = async () => {
      try {
        const resp = await apiClient.get("/help-center/category-groups");
        // console.log("This is data", resp);
      } catch (error) {}
    };
    FetchData();
  }, []);
  // Replace your commented useEffect with this:
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Fetch categories with published pages
        const res = await apiClient.get("/help-center/category-groups");
        const data = res.data?.data || [];

        // console.log("Categories data:", data); // Check the structure

        // Filter to only include categories with published pages
        const publishedGroups = data
          .map((group) => ({
            ...group,
            categories: (group.categories || [])
              .map((cat) => ({
                ...cat,
                pages: (cat.pages || [])
                  .filter((p) => p.status === "published")
                  .map((p) => ({
                    ...p,
                    slug: p.slug, // Explicitly include slug
                  })),
              }))
              .filter((cat) => cat.pages.length > 0),
          }))
          .filter((group) => group.categories.length > 0);

        setCategories(publishedGroups);

        // Auto-expand categories that contain current page
        publishedGroups.forEach((group) => {
          group.categories.forEach((cat) => {
            if (cat.pages?.some((p) => p.id === Number(slug))) {
              setOpenCategories((prev) => ({
                ...prev,
                [cat.id]: true,
                [group.id]: true,
              }));
            }
          });
        });
      } catch (err) {
        console.error("Category fetch error:", err);
      }
    };

    if (slug) {
      fetchCategories();
    }
  }, [slug]);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 5;

    const extractHeadingsWithRetry = () => {
      if (!isMounted) return;

      if (!contentRef.current || contentBlocks.length === 0) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(extractHeadingsWithRetry, 200 * retryCount);
        }
        return;
      }

      const elements = contentRef.current.querySelectorAll(
        "h1, h2, h3, h4, h5, h6",
      );

      if (elements.length === 0) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(extractHeadingsWithRetry, 200 * retryCount);
        }
        return;
      }

      const list = Array.from(elements).map((el, index) => {
        if (!el.id) {
          const textSlug = el.innerText
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
          el.id = textSlug || `heading-${index}-${Date.now()}`;
        }

        return {
          id: el.id,
          text: el.innerText.trim(),
          level: Number(el.tagName[1]),
          element: el,
        };
      });

      // console.log(`Headings for page ${id}:`, list);
      setHeadings(list);

      if (list.length > 0) {
        setActiveId(list[0].id);
      }
    };

    // Reset and start extraction
    setHeadings([]);
    setActiveId("");
    extractHeadingsWithRetry();

    return () => {
      isMounted = false;
    };
  }, [slug, contentBlocks]); // Depend on id and contentBlocks
  // scroll spy
  // useEffect(() => {
  //     if (!headings.length) return;

  //     const observer = new IntersectionObserver(
  //         (entries) => {
  //             entries.forEach((entry) => {
  //                 if (entry.isIntersecting) {
  //                     setActiveId(entry.target.id);
  //                 }
  //             });
  //         },
  //         { rootMargin: "-100px 0px -50% 0px" }
  //     );

  //     headings.forEach((h) => observer.observe(h.element));
  //     return () => headings.forEach((h) => observer.unobserve(h.element));
  // }, [headings]);
  // console.log("Is this Coming or not", page);

  useEffect(() => {
    if (!headings.length) return;

    // ✅ detect the actual scroll container (window OR your scrollable parent)
    const rootScroller = headings[0]?.element
      ? getScrollParent(headings[0].element)
      : window;

    const observer = new IntersectionObserver(
      (entries) => {
        // ✅ pick the most visible intersecting heading (stable like Linear)
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0),
          );

        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        root: rootScroller === window ? null : rootScroller, // ✅ critical
        threshold: [0.15, 0.25, 0.4, 0.6],
        rootMargin: "-100px 0px -60% 0px",
      },
    );

    headings.forEach((h) => h.element && observer.observe(h.element));

    return () => observer.disconnect();
  }, [headings]);
  useEffect(() => {
    if (!activeId) return;

    const el = tocItemRefs.current[activeId];
    if (!el) return;

    // ✅ real top position inside TOC container (no fixed 32px assumption)
    const height = 20;
    const top = el.offsetTop + (el.offsetHeight - height) / 2;

    setTocIndicator({ top, height });
  }, [activeId, headings.length]);
  // Back to top
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleCategory = (catId) => {
    setOpenCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const handleBookmark = () => {
    const newBookmarked = !bookmarked;
    setBookmarked(newBookmarked);

    const bookmarks = JSON.parse(localStorage.getItem("docBookmarks") || "[]");
    if (newBookmarked) {
      bookmarks.push({
        id: page.id,
        title: page.title,
        url: `/docs/${page.id}`,
      });
    } else {
      const index = bookmarks.findIndex((b) => b.id === page.id);
      if (index > -1) bookmarks.splice(index, 1);
    }
    localStorage.setItem("docBookmarks", JSON.stringify(bookmarks));
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: page.title,
          text: `Check out this documentation: ${page.title}`,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Share cancelled:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const filteredCategories = categories.filter(
    (cat) =>
      searchQuery === "" ||
      cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.pages?.some((p) =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
  );

  // const { prevPage, nextPage } = useMemo(() => {
  //   const currentId = String(id);
  //   const flat = [];

  //   for (const cat of categories || []) {
  //     for (const p of cat.pages || []) {
  //       flat.push({
  //         id: p.id,
  //         title: p.title,
  //         __catId: cat.id,
  //         __catName: cat.name,
  //       });
  //       console.log("Data su aave", p.title);
  //     }
  //   }

  //   if (!flat.length) return { prevPage: null, nextPage: null };

  //   const idx = flat.findIndex((p) => String(p.id) === currentId);
  //   if (idx === -1) return { prevPage: null, nextPage: flat[0] ?? null };

  //   return {
  //     prevPage: idx > 0 ? flat[idx - 1] : null,
  //     nextPage: idx < flat.length - 1 ? flat[idx + 1] : null,
  //   };
  // }, [categories, id]);

  const { prevPage, nextPage } = useMemo(() => {
    const currentId = String(slug);
    // console.log("CURRENT", currentId);
    const flat = (categories || []) // now this = groups
      .sort((g1, g2) => g1.order_index - g2.order_index) // group order
      .flatMap((group) =>
        (group.categories || [])
          .sort((c1, c2) => c1.order_index - c2.order_index) // category order
          .flatMap((cat) =>
            (cat.pages || [])
              .filter((p) => p.status === "published") // ✅ IMPORTANT
              .sort((p1, p2) => p1.order_index - p2.order_index) // page order
              .map((p) => ({
                id: p.id,
                slug: p.slug,
                title: p.title,
                __groupName: group.group_name,
                __catName: cat.name,
              })),
          ),
      );

    // console.log("Flat", flat);

    if (!flat.length) return { prevPage: null, nextPage: null };

    const idx = flat.findIndex((p) => String(p.slug) === currentId);

    if (idx === -1) {
      return { prevPage: null, nextPage: flat[0] ?? null };
    }

    return {
      prevPage: idx > 0 ? flat[idx - 1] : null,
      nextPage: idx < flat.length - 1 ? flat[idx + 1] : null,
    };
  }, [categories, slug]);
  /* ✅ Skeleton loading screen */
  if (loading) return <DocsPageSkeleton />;

  if (error || !page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl shadow-lg p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="text-red-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
            Something went wrong
          </h2>
          <p className="text-gray-600 text-center mb-2">
            {error ||
              "The documentation page you're looking for doesn't exist."}
          </p>
          <p className="text-sm text-gray-500 text-center mb-8">
            Error ID: {slug}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              type="button"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/docs")}
              className="block w-full py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-center"
              type="button"
            >
              Browse All Documentation
            </button>
            <a
              href="mailto:support@example.com"
              className="block w-full py-3 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors text-center"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="flex">
        {/* OVERLAY for mobile sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-30 lg:hidden">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setSidebarOpen(false)}
            />
          </div>
        )}
        {/* MAIN */}
        <main className="flex-1 min-w-0">
          {/* ReDesign css ma style css*/}
          <div className="docs-editor-pages-resp">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                {/* border-b border-gray-100 */}
                <h1 className="Docs_Page ">{page.heading}</h1>
                {/* <hr className="text-red h-3" /> */}
                {page.summary && (
                  <p className="text-xl text-gray-600 mb-6">{page.summary}</p>
                )}
                {/* hidden sm:flex gap-2 mb-6 flex-wrap justify-center */}
                {/* {console.log("Pages is there", page)} */}
              </div>
            </div>

            <div className="relative">
              <div
                ref={contentRef}
                className={`prose max-w-none ${
                  viewMode === "comfort"
                    ? "prose-lg"
                    : viewMode === "compact"
                      ? "prose-sm"
                      : "prose-base"
                }`}
              >
                {contentBlocks.length === 0 && (
                  <>
                    <InfoBox type="note">
                      This is a sample documentation page. Add your content
                      using the BlockNote editor to see it rendered here
                      beautifully.
                    </InfoBox>

                    <Step number="1" title="Getting Started">
                      <p>
                        Begin by understanding the basic concepts and
                        requirements for using our platform.
                      </p>
                      <InfoBox type="tip">
                        Make sure you have the latest version installed for the
                        best experience.
                      </InfoBox>
                    </Step>

                    <Step number="2" title="Configuration">
                      <p>
                        Configure your settings according to your needs. Here's
                        a sample configuration:
                      </p>
                      <pre className="bg-gray-800 text-gray-200 p-4 rounded-lg overflow-x-auto">
                        <code>{`{
  "apiKey": "your-api-key-here",
  "environment": "production",
  "features": ["analytics", "notifications"]
}`}</code>
                      </pre>
                    </Step>

                    <Step number="3" title="Implementation">
                      <p>
                        Implement the solution following best practices and
                        guidelines.
                      </p>
                      <InfoBox type="warning">
                        Always test in a development environment before
                        deploying to production.
                      </InfoBox>
                    </Step>
                  </>
                )}

                {contentBlocks.length > 0 &&
                  contentBlocks.map((block, idx) => (
                    <React.Fragment key={`block-${idx}`}>
                      {renderBlock(block, idx)}
                    </React.Fragment>
                  ))}
              </div>
            </div>
          </div>

          {/* Prev/Next */}
          <div className="btn_side">
            <button
              className={`navCard navCard--prev ${!prevPage ? "is-disabled" : ""}`}
              type="button"
              // disabled={!prevPage}
              disabled={!prevPage || !prevPage.slug}
              onClick={() => {
                if (prevPage && prevPage.slug) {
                  // console.log("Navigating to prev:", prevPage.slug);
                  navigate(`/docs/${prevPage.slug}`);
                }
              }}
              // onClick={() =>
              //   nextPage && navigate("/docs", { state: { id: prevPage.id } })
              // }
            >
              {/* {console.log("Prev Page", prevPage)} */}
              <span className="navCard__top">
                <span className="navCard__arrow" aria-hidden>
                  ‹
                </span>
                <span className="navCard__label">Previous</span>
              </span>
              <span className="navCard__title">
                {prevPage ? (
                  <>
                    <div className="text-xs text-gray-400 mb-1">
                      {/* {prevPage.__groupName} • */}
                      {prevPage.__catName}
                    </div>
                    {prevPage.title}
                  </>
                ) : (
                  "—"
                )}
              </span>
            </button>

            <button
              className={`navCard navCard--next ${!nextPage ? "is-disabled" : ""}`}
              type="button"
              disabled={!nextPage || !nextPage.slug}
              onClick={() => {
                if (nextPage && nextPage.slug) {
                  // console.log("Navigating to next:", nextPage.slug);
                  navigate(`/docs/${nextPage.slug}`);
                }
              }}
              // onClick={() =>
              //   nextPage && navigate("/docs", { state: { id: nextPage.id } })
              // }
            >
              {/* {console.log("Slug", nextPage.slug)} */}
              <span className="navCard__top">
                <span className="navCard__label">Next</span>
                <span className="navCard__arrow" aria-hidden>
                  ›
                </span>
              </span>
              <span className="navCard__title">
                {/* <div className="text-xs text-gray-400 mb-1">
                  {nextPage.__catName}
                </div>
                {nextPage ? nextPage.title : "—"} */}
                {nextPage ? (
                  <>
                    <div className="text-xs text-gray-400 mb-1">
                      {/* {prevPage.__groupName} • */}
                      {nextPage.__catName}
                    </div>
                    {nextPage.title}
                  </>
                ) : (
                  "—"
                )}
              </span>
            </button>
          </div>

          {showBackToTop && (
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              // className="fixed bottom-8 right-8 p-3 bg-gradient-to-r from-[#093A5B]/50 to-[093A5B]/90 text-white rounded-full shadow-xl hover:shadow-2xl transition-all z-40 hover:scale-110"
              // className="fixed bottom-8 right-8 p-3 bg-[#093A5B] text-white rounded-full shadow-lg hover:bg-[#0b4a73] hover:shadow-xl transition-all duration-200 z-40 hover:scale-105"
              className="floating-support-btn"
              aria-label="Back to top"
              // type="button"
            >
              <ArrowUp size={20} />
            </button>
          )}
        </main>
        {headings.length > 0 && (
          <aside className="hidden xl:block w-80 pl-8 pr-4 mr-20 py-8 relative ReDesign">
            <div
              className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto"
              data-custom-scrollbar
            >
              <div className="relative">
                {/* ✅ Active indicator line (GRAY) */}
                {activeId && (
                  <div
                    style={{ background: "#e3eaf7" }}
                    className="absolute left-0 w-[2px] h-full rounded-full transition-all duration-200"
                  >
                    <div
                      className="absolute left-0 w-[2px] rounded-full transition-all duration-200"
                      style={{
                        top: `${tocIndicator.top}px`,
                        height: `${tocIndicator.height}px`,
                        background: "#093A5B", // ✅ gray-400 (visible like Linear)
                        // #9CA3AF
                      }}
                    />
                  </div>
                )}
                <nav className="space-y-1 relative">
                  {headings.map((h) => {
                    const marginLeft = h.level >= 2 ? (h.level - 1) * 18 : 10;

                    const fontWeight =
                      h.level === 1
                        ? "font-semibold"
                        : h.level === 2
                          ? "font-medium"
                          : "font-normal";

                    const isActive = activeId === h.id;

                    return (
                      <a
                        key={h.id}
                        ref={(node) => {
                          if (node) tocItemRefs.current[h.id] = node;
                        }}
                        href={`#${h.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToHeading(h.id);
                        }}
                        className={[
                          "block rounded-lg transition-all group",
                          fontWeight,
                          // isActive
                          //     ? "bg-gray-100 text-gray-900"
                          //     : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                        ].join(" ")}
                        style={{ marginLeft: `${marginLeft}px` }}
                      >
                        <div className="flex items-center">
                          <span
                            className={`Indicator_Class ${isActive ? "text-[#093A5B] font-bold" : ""}`}
                          >
                            {h.text}
                          </span>
                        </div>
                      </a>
                    );
                  })}
                </nav>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
